import { nextTick, onMounted, onUnmounted, shallowRef } from 'vue'

// Подсветка ячеек графика после копирования смен дня из редактора (RD-140).
// Состояние общее для страницы: панель редактора к этому моменту уже закрыта и размонтирована.
interface CopyHighlight {
    employmentId: string
    dates: Set<string>
    fading: boolean
}

const HIGHLIGHT_MS = 2000
const FADE_MS = 400

const highlight = shallowRef<CopyHighlight | null>(null)
let fadeTimer: ReturnType<typeof setTimeout> | undefined
let clearTimer: ReturnType<typeof setTimeout> | undefined
// Поколение контекста сетки: растёт при каждом сбросе (смена периода/подразделения, размонтирование таблицы).
// Операция копирования запоминает его при старте; запоздалый результат прежнего контекста не подсвечивается.
let contextGeneration = 0
// Смонтированные таблицы графика: без них (уход со страницы, табель) подсветка не создаётся.
let mountedSurfaces = 0

export const copyCellKey = (employmentId: string, date: string) => `${employmentId}|${date}`

function stopTimers() {
    clearTimeout(fadeTimer)
    clearTimeout(clearTimer)
    fadeTimer = undefined
    clearTimer = undefined
}

export function clearCopyHighlight() {
    contextGeneration += 1
    stopTimers()
    highlight.value = null
}

export const currentCopyHighlightContext = () => contextGeneration

// Таблица графика: регистрируется как поверхность подсветки и сбрасывает её при размонтировании.
export function useCopyHighlightSurface() {
    onMounted(() => { mountedSurfaces += 1 })
    onUnmounted(() => {
        mountedSurfaces = Math.max(0, mountedSurfaces - 1)
        clearCopyHighlight()
    })
}

// Класс ячейки по точной дате и employment ID (не по индексу отфильтрованной строки).
export function copyHighlightClass(employmentId: string, date: string) {
    const current = highlight.value
    if (!current || current.employmentId !== employmentId || !current.dates.has(date)) return undefined
    return current.fading ? 'ds-copy-highlight ds-copy-highlight-fading' : 'ds-copy-highlight'
}

// Если ячейка не видна целиком — прокрутить к ней ближайшим путём. Не отрисованные цели не ищем.
function scrollToCell(employmentId: string, date: string) {
    const selector = `[data-copy-cell="${CSS.escape(copyCellKey(employmentId, date))}"]`
    const cell = document.querySelector<HTMLElement>(selector)
    if (!cell) return
    const rect = cell.getBoundingClientRect()
    const isVisible = rect.top >= 0 && rect.left >= 0
        && rect.bottom <= document.documentElement.clientHeight
        && rect.right <= document.documentElement.clientWidth
    if (!isVisible) cell.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
}

// context — поколение на момент старта операции; проверяется непосредственно перед созданием эффекта.
export async function highlightCopiedCells(employmentId: string, dates: string[], context: number) {
    if (context !== contextGeneration || mountedSurfaces === 0) return
    stopTimers()
    if (!dates.length) {
        highlight.value = null
        return
    }
    highlight.value = { employmentId, dates: new Set(dates), fading: false }
    fadeTimer = setTimeout(() => {
        if (highlight.value) highlight.value = { ...highlight.value, fading: true }
        clearTimer = setTimeout(() => { highlight.value = null }, FADE_MS)
    }, HIGHLIGHT_MS)

    await nextTick()
    if (context !== contextGeneration) return
    scrollToCell(employmentId, [...dates].sort()[0])
}
